import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';
import { Trash2 } from 'lucide-react';
import { API } from '../../App';

const AdminOrdersPage = ({ adminToken }) => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deletingOrder, setDeletingOrder] = useState(null);

  useEffect(() => {
    fetchOrders();
  }, []);

  const fetchOrders = async () => {
    try {
      const response = await axios.get(`${API}/admin/orders`, {
        headers: { Authorization: `Bearer ${adminToken}` }
      });
      setOrders(response.data);
    } catch (error) {
      console.error('Failed to fetch orders', error);
      toast.error('Failed to load orders');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateStatus = async (orderId, field, value) => {
    try {
      await axios.put(
        `${API}/admin/orders/${orderId}/status`,
        { [field]: value },
        { headers: { Authorization: `Bearer ${adminToken}` } }
      );
      toast.success('Order status updated!');
      fetchOrders();
    } catch (error) {
      console.error('Failed to update order', error);
      toast.error('Failed to update order');
    }
  };

  const handleDeleteOrder = async (orderId, orderNumber) => {
    if (!window.confirm(`Are you sure you want to delete Order #${orderNumber}? This action cannot be undone.`)) {
      return;
    }
    
    setDeletingOrder(orderId);
    try {
      await axios.delete(
        `${API}/admin/orders/${orderId}`,
        { headers: { Authorization: `Bearer ${adminToken}` } }
      );
      toast.success(`Order #${orderNumber} deleted successfully`);
      fetchOrders();
    } catch (error) {
      console.error('Failed to delete order', error);
      toast.error('Failed to delete order');
    } finally {
      setDeletingOrder(null);
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center min-h-screen">Loading...</div>;
  }

  return (
    <div className="py-8" data-testid="admin-orders-page">
      <div className="mb-8">
        <h1 className="text-4xl font-heading font-bold mb-2">Orders Management</h1>
        <p className="text-muted-foreground">View and manage all customer orders</p>
      </div>

      {orders.length === 0 ? (
        <div className="text-center py-12 bg-white border border-border">
          <p className="text-lg text-muted-foreground">No orders yet</p>
        </div>
      ) : (
        <div className="space-y-4">
          {orders.map((order) => (
            <motion.div
              key={order.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-white border border-border p-6"
              data-testid={`order-${order.id}`}
            >
              <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between mb-4">
                <div>
                  <h3 className="text-xl font-heading font-semibold mb-1">Order #{order.order_number}</h3>
                  <p className="text-sm text-muted-foreground">
                    {new Date(order.created_at).toLocaleString()}
                  </p>
                  <p className="text-sm text-muted-foreground">User ID: {order.user_id}</p>
                </div>
                <div className="mt-4 lg:mt-0 flex flex-col space-y-2">
                  <div>
                    <label className="text-sm text-muted-foreground mr-2">Payment Status:</label>
                    <select
                      value={order.payment_status}
                      onChange={(e) => handleUpdateStatus(order.id, 'payment_status', e.target.value)}
                      className="border border-border px-3 py-1 outline-none focus:border-primary"
                      data-testid={`payment-status-${order.id}`}
                    >
                      <option value="pending">Pending</option>
                      <option value="paid">Paid</option>
                      <option value="failed">Failed</option>
                    </select>
                  </div>
                  <div>
                    <label className="text-sm text-muted-foreground mr-2">Order Status:</label>
                    <select
                      value={order.order_status}
                      onChange={(e) => handleUpdateStatus(order.id, 'order_status', e.target.value)}
                      className="border border-border px-3 py-1 outline-none focus:border-primary"
                      data-testid={`order-status-${order.id}`}
                    >
                      <option value="pending">Pending</option>
                      <option value="confirmed">Confirmed</option>
                      <option value="processing">Processing</option>
                      <option value="shipped">Shipped</option>
                      <option value="delivered">Delivered</option>
                      <option value="cancelled">Cancelled</option>
                    </select>
                  </div>
                  <button
                    onClick={() => handleDeleteOrder(order.id, order.order_number)}
                    disabled={deletingOrder === order.id}
                    className="flex items-center justify-center gap-2 bg-red-600 hover:bg-red-700 text-white py-2 px-4 text-sm font-semibold transition-colors disabled:opacity-50 mt-2"
                    data-testid={`delete-order-${order.id}`}
                  >
                    <Trash2 size={16} className={deletingOrder === order.id ? 'animate-pulse' : ''} />
                    {deletingOrder === order.id ? 'Deleting...' : 'Delete Order'}
                  </button>
                </div>
              </div>

              <div className="border-t border-border pt-4">
                <h4 className="font-semibold mb-3">Order Items:</h4>
                <div className="space-y-2">
                  {order.items.map((item, index) => (
                    <div key={index} className="flex justify-between text-sm">
                      <span>
                        {item.product_name} ({item.variant_sku}) x {item.quantity}
                      </span>
                      <span className="font-semibold">&#8377;{(item.price * item.quantity).toFixed(2)}</span>
                    </div>
                  ))}
                </div>
                <div className="mt-4 pt-4 border-t border-border space-y-1">
                  {order.subtotal > 0 && (
                    <>
                      <div className="flex justify-between text-sm">
                        <span className="text-muted-foreground">Subtotal:</span>
                        <span>&#8377;{(order.subtotal || 0).toFixed(2)}</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-muted-foreground">GST (18%):</span>
                        <span>&#8377;{(order.gst_amount || 0).toFixed(2)}</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-muted-foreground">Shipping:</span>
                        <span>&#8377;{(order.shipping_charges || 0).toFixed(2)}</span>
                      </div>
                    </>
                  )}
                  <div className="flex justify-between pt-2">
                    <span className="font-semibold">Grand Total:</span>
                    <span className="text-lg font-bold text-primary">&#8377;{order.total_amount.toFixed(2)}</span>
                  </div>
                </div>
                <div className="mt-2">
                  <span className="text-sm text-muted-foreground">Payment Method:</span>
                  <span className="text-sm font-medium ml-2">{order.payment_method}</span>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      )}
    </div>
  );
};

export default AdminOrdersPage;
